// useSwipe.js
import { onMounted, onBeforeUnmount } from '@nuxtjs/composition-api'

/**
 * Minimal swipe detector (pointer events w/ touch fallback).
 *
 * @param {HTMLElement|Ref<HTMLElement>|() => HTMLElement|null} target
 *   The element (or ref / getter) to attach listeners to.
 *
 * @param {Object} opts
 * @param {number}   opts.threshold    Min distance (px) in the swipe axis to count (default 50)
 * @param {number}   opts.restraint    Max perpendicular travel (px) allowed (default 100)
 * @param {number}   opts.allowedTime  Max duration (ms) for a valid swipe (default 300)
 * @param {Function} opts.onSwipe      Callback: (dir, event) where dir ∈ 'left'|'right'|'up'|'down'
 */
export function useSwipe(target, opts = {}) {
  const {
    threshold = 50,
    restraint = 100,
    allowedTime = 300,
    onSwipe = () => {},
  } = opts

  // ──────────────────────
  // Internal state
  // ──────────────────────
  let el = null            // the EventTarget we actually bound to
  let usingPointer = false // whether we used Pointer Events or touch fallback
  let pointerId = null     // ID of the active pointer (to ignore secondary pointers)

  let startX = 0
  let startY = 0
  let startAt = 0          // timestamp (ms) when the gesture started

  // ──────────────────────
  // Helpers
  // ──────────────────────
  const resolveElement = () => {
    // Accept a getter, a ref, or a plain element
    const candidate = typeof target === 'function'
      ? target()
      : (target && 'value' in Object(target) ? target.value : target)

    return candidate && typeof candidate.addEventListener === 'function'
      ? candidate
      : null
  }

  const getDirection = (dx, dy, elapsed) => {
    if (elapsed > allowedTime) return null

    const absX = Math.abs(dx)
    const absY = Math.abs(dy)

    // Horizontal swipe
    if (absX >= threshold && absY <= restraint) {
      return dx < 0 ? 'left' : 'right'
    }
    // Vertical swipe
    if (absY >= threshold && absX <= restraint) {
      return dy < 0 ? 'up' : 'down'
    }
    return null
  }

  const setTouchAction = (node) => {
    // Prefer CSS `.swipe-surface { touch-action: pan-y; }`
    // but set here if missing to avoid accidental pass-through.
    try {
      if (node && node.style && !node.style.touchAction) {
        node.style.touchAction = 'pan-y'
      }
    } catch (e) {
      // non-fatal
      // console.warn('useSwipe touch-action set failed', e)
    }
  }

  // ──────────────────────
  // Pointer handlers (mouse/touch/pen)
  // ──────────────────────
  const onPointerDown = (e) => {
    // Ignore if already tracking; ignore non-primary mouse buttons
    if (pointerId !== null || (e.pointerType === 'mouse' && e.button !== 0)) return
    pointerId = e.pointerId ?? 'mouse'

    startX = e.clientX
    startY = e.clientY
    startAt = performance.now()
  }

  const onPointerUp = (e) => {
    if (pointerId === null || e.pointerId !== pointerId) return

    const elapsed = performance.now() - startAt
    const dx = e.clientX - startX
    const dy = e.clientY - startY
    pointerId = null

    const dir = getDirection(dx, dy, elapsed)
    if (dir) onSwipe(dir, e)
  }

  const onPointerCancel = (e) => {
    if (pointerId !== null && e.pointerId === pointerId) pointerId = null
  }

  // ──────────────────────
  // Touch fallback (old Safari, no Pointer Events)
  // ──────────────────────
  let lastTouchX = 0
  let lastTouchY = 0
  let touchStartAt = 0

  const onTouchStart = (t) => {
    const touch = t.changedTouches[0]
    lastTouchX = touch.clientX
    lastTouchY = touch.clientY
    touchStartAt = performance.now()
  }

  const onTouchEnd = (t) => {
    const touch = t.changedTouches[0]
    const dx = touch.clientX - lastTouchX
    const dy = touch.clientY - lastTouchY
    const elapsed = performance.now() - touchStartAt

    const dir = getDirection(dx, dy, elapsed)
    if (dir) onSwipe(dir, t)
  }

  // ──────────────────────
  // Bind / Unbind
  // ──────────────────────
  const addListeners = (node) => {
    if (!node || typeof node.addEventListener !== 'function') return

    setTouchAction(node)

    if (process.client && 'onpointerdown' in window) {
      usingPointer = true
      node.addEventListener('pointerdown', onPointerDown, { passive: true })
      node.addEventListener('pointerup', onPointerUp, { passive: true })
      node.addEventListener('pointercancel', onPointerCancel, { passive: true })
      node.addEventListener('pointerleave', onPointerCancel, { passive: true })
    } else {
      usingPointer = false
      node.addEventListener('touchstart', onTouchStart, { passive: true })
      node.addEventListener('touchend', onTouchEnd, { passive: true })
    }
  }

  const removeListeners = (node) => {
    if (!node || typeof node.removeEventListener !== 'function') return

    if (usingPointer) {
      node.removeEventListener('pointerdown', onPointerDown)
      node.removeEventListener('pointerup', onPointerUp)
      node.removeEventListener('pointercancel', onPointerCancel)
      node.removeEventListener('pointerleave', onPointerCancel)
    } else {
      node.removeEventListener('touchstart', onTouchStart)
      node.removeEventListener('touchend', onTouchEnd)
    }
  }

  // ──────────────────────
  // Lifecycle
  // ──────────────────────
  onMounted(() => {
    el = resolveElement()
    if (!el) return
    addListeners(el)
  })

  onBeforeUnmount(() => {
    removeListeners(el)
    el = null
  })

  // Optional: expose a manual teardown if you want to stop early
  return {
    stop() {
      removeListeners(el)
      el = null
    },
  }
}
